import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '../lib/cn.js';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover-elevate active-elevate-2',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground border border-primary-border shadow-xs',
        destructive: 'bg-destructive text-destructive-foreground border border-destructive-border',
        // Laisse voir le fond de la carte, de la barre latérale ou de la zone
        // dans laquelle il se trouve, et hérite de la couleur de texte courante.
        outline: 'border [border-color:var(--button-outline)] shadow-2xs active:shadow-none',
        secondary: 'border bg-secondary text-secondary-foreground border-secondary-border',
        // Bordure transparente : activer une bordure plus tard ne décale pas la mise en page.
        ghost: 'border border-transparent',
      },
      // Hauteurs minimales : un bouton au contenu inhabituellement long grandit
      // au lieu de déborder.
      size: {
        default: 'min-h-9 px-4 py-2',
        sm: 'min-h-8 rounded-md px-3 text-xs',
        lg: 'min-h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { buttonVariants };
